import {Component, OnDestroy, OnInit} from '@angular/core';
import {filter, Observable, Subscription, switchMap, take, timer} from 'rxjs';
import {IconRegistryService} from './shared/services/icon-registry.service';
import {VisibilityService} from './shared/services/visibility.service';
import {Store} from '@ngrx/store';
import {GetAlertsCountAction} from './core/store/alerts/alerts.action';
import {AlertsActiveService} from './shared/services/alerts-active.service';
import {User} from './core/model/user';
import {selectActiveUser} from './core/store/active-user/active-user.selector';
import {OsNotificationService} from './services/os-notification.service';
import {MobileService} from './core/page/mobile.service';
import {MobileBackButtonService} from './core/services/mobile-back-button.service';

@Component({
  selector: 'oph-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Ophanim';

  private activitySubscription: Subscription;
  private alertsActiveSubscription: Subscription;
  private timerSubscription: Subscription;
  user$: Observable<User> = this.store$.select(selectActiveUser);
  timerIntervalMinutes = 1;

  constructor(
    private iconRegistryService: IconRegistryService,
    private visibilityService: VisibilityService,
    private alertsActiveService: AlertsActiveService,
    private store$: Store,
    private osNotificationService: OsNotificationService,
    private mobileService: MobileService,
    private mobileBackButtonService: MobileBackButtonService
  ) {}

  public ngOnInit(): void {
    this.iconRegistryService.registerIcons();
    this.setupActivityMonitoring();
    this.setUpMobileConfig();

    this.alertsActiveSubscription = this.alertsActiveService.getAlertsActive().subscribe(isActive => {
      if (isActive) {
        this.stopTimer();
      } else if (this.visibilityService.isActive()) {
        this.startTimer();
      }
    });
  }

  /**
   * Configures mobile-specific settings for iOS and Android platforms.
   *
   * @remarks
   * This method will return early without performing any operations if the platform
   * is not iOS or Android (e.g., web, desktop).
   *
   * @returns {void}
   */
  setUpMobileConfig(): void {
    const isMobile = this.mobileService.isMobilePlatform();
    if (!isMobile) return;

    /**
     * Adds a platform-specific class to the body element.
     * Adds "is-mobile-ios" for iOS or "is-mobile-android" for Android.
     * This allows for platform-specific CSS styling.
     */
    const platform = this.mobileService.getMobilePlatform();
    if (platform === 'ios') {
      document.body.classList.add('is-mobile-ios');
    } else if (platform === 'android') {
      document.body.classList.add('is-mobile-android');
    }

    this.osNotificationService.startDailyReminder();
    this.mobileBackButtonService.initializeBackButton();
  }

  public ngOnDestroy(): void {
    // Cleanup subscriptions
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
    }
    if (this.alertsActiveSubscription) {
      this.alertsActiveSubscription.unsubscribe();
    }
    this.stopTimer();

    const isMobile = this.mobileService.isMobilePlatform();
    if (isMobile) {
      this.osNotificationService.stopDailyReminder();
      this.mobileBackButtonService.destroy();
    }
  }

  private setupActivityMonitoring(): void {
    // Subscribe to the combined state - only active when visible, focused, and mouse has been active
    this.activitySubscription = this.visibilityService.getActiveState().subscribe(isActive => {
      if (isActive && !this.alertsActiveService.isAlertsActive()) {
        this.startTimer();
      } else {
        this.stopTimer();
      }
    });

    // Start the timer immediately if the app is visible, focused, and mouse is active
    if (this.visibilityService.isActive() && !this.alertsActiveService.isAlertsActive()) {
      this.startTimer();
    }
  }

  private startTimer(): void {
    this.stopTimer();

    this.timerSubscription = timer(0, this.timerIntervalMinutes * 60000)
      .pipe(
        switchMap(() =>
          this.user$.pipe(
            filter(user => !!user),
            take(1)
          )
        )
      )
      .subscribe((_user: User) => {
        this.getAlertsCount();
      });
  }

  private stopTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  private getAlertsCount(): void {
    this.store$.dispatch(new GetAlertsCountAction());
  }
}
